import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';
import axios from 'axios';
import api from '../utils/api';
import { SearchContext } from './complexContexts';
import { ActiveCatalogContext } from './ActiveCatalogContext';
import { serializeScopes, type SearchScope } from '../utils/searchScopes';
import { logger } from '../utils/logger';

export type SortBy =
  | 'relevance'
  | 'newest'
  | 'oldest'
  | 'title_asc'
  | 'title_desc'
  | 'year_asc'
  | 'year_desc'
  | 'catalog_asc'
  | 'catalog_desc'
  | 'custom_asc'
  | 'custom_desc';

export type { SearchScope };

export interface FacetBucket {
  value: string;
  count: number;
  /** Populated only on `catalog` facet delegate rows (delegated catalog name). */
  label?: string | null;
  /** Populated only on `catalog` facet parent rows that expand to Public/Private. */
  children?: FacetBucket[] | null;
}

export interface SearchFilters {
  catalogId?: string;
  aliId?: string;
  username?: string;
  workType?: string[];
  materials?: string[];
  techniques?: string[];
  creators?: string[];
  decade?: string[];
  creationYear?: { min?: number; max?: number };
  visibility?: 'public' | 'private';
  title?: string;
  keywords?: string;
  provenance?: string;
  heightFrom?: number;
  heightTo?: number;
  widthFrom?: number;
  widthTo?: number;
  dateFrom?: number;
  dateTo?: number;
  sortBy?: SortBy;
  /** Opt-in facet aggregations to request from the backend. */
  requestedFacets?: string[];
  /**
   * Explicit catalog scopes to search. When omitted, the backend preserves
   * legacy "caller's items UNION any public" behaviour. Supports simple
   * 'own' / 'public' / 'delegate' tokens plus a structured per-collection
   * delegate form — see utils/searchScopes for the wire format.
   */
  scopes?: SearchScope[];
}

export interface SearchResult {
  catalogItemId: string;
  userId: string;
  title: string;
  creators: string[];
  workType: string;
  materials: string[];
  techniques: string[];
  keywords: string[];
  coverImageUrl?: string;
  aliCatalogNumber?: string;
  visibility: 'public' | 'private';
  creationYear?: number;
  updatedAt: string;
  highlight?: Record<string, string[]>;
}

export interface SearchFacets {
  workTypes: Array<{ key: string; count: number }>;
  materials: Array<{ key: string; count: number }>;
  techniques: Array<{ key: string; count: number }>;
  creators: Array<{ key: string; count: number }>;
}

const DEFAULT_PAGE_SIZE = 12;

const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const activeCatalogCtx = useContext(ActiveCatalogContext);
  const activeCatalogOwnerId = activeCatalogCtx?.activeCatalogOwnerId ?? null;
  // PR 4b — when the active catalog is one of the user's owned add-ons
  // (or the primary, when an explicit selection has been made), scope the
  // search to it via the catalogAliId query param shipped in PR 4a. Null
  // for delegated catalogs (those route through owner_id instead) or when
  // no explicit selection has been made.
  const activeCatalogAliId = activeCatalogCtx?.activeCatalogAliId ?? null;

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [facets] = useState<SearchFacets | null>(null);
  const [facetBuckets, setFacetBuckets] = useState<Record<string, FacetBucket[]> | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<SortBy>('relevance');
  const [pageSize, setPageSizeState] = useState<number>(DEFAULT_PAGE_SIZE);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(0);
  }, []);

  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const search = useCallback(async (query: string, filters?: SearchFilters): Promise<SearchResult[]> => {
    if (!api) throw new Error('API not configured');

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Clear stale results synchronously so the loading state shows a clean
    // grid instead of last query's cards lingering until the new response
    // resolves. Without this, submitting a new query while staying on /search
    // displays the previous query's results — confusing UX that looks like
    // "random items" until the request settles.
    setSearchResults([]);
    setFacetBuckets(null);
    setTotal(0);
    setIsSearching(true);
    setError(null);

    try {
      // Build query parameters for GET request
      const params = new URLSearchParams();

      // Explicit `scopes` mean a GLOBAL search across everything the caller can
      // access (own ∪ public ∪ delegated). The active-catalog params below
      // (owner_id / catalogAliId) AND-narrow to a single catalog, which would
      // defeat that union — so suppress them whenever scopes are present.
      // Mirrors the backend's `not scopes` guard (search_service_pg.py) and
      // avoids the owner_id+scopes interaction where the `own` scope would
      // otherwise resolve to the delegator's items instead of the caller's.
      const hasExplicitScopes = !!(filters?.scopes && filters.scopes.length > 0);

      // When in delegate mode, pass the delegator's user ID so the backend
      // searches the delegated catalog's private index instead of the
      // delegate's own (empty) catalog. Fixes #993.
      if (!hasExplicitScopes && activeCatalogOwnerId) {
        params.append('owner_id', activeCatalogOwnerId);
      }

      // PR 4b — when the user has explicitly selected one of their owned
      // catalogs (primary or add-on), narrow the search to that catalog's
      // LL-NXX via the per-catalog filter shipped in PR 4a. Skipped for
      // delegated catalogs (owner_id above handles the per-user scope).
      // The format guard defends against localStorage corruption — the
      // backend would 400 on a malformed value but the resulting "no
      // results" UX wouldn't surface the cause. Mirrors the strict
      // LL-NXX regex enforced server-side.
      if (!hasExplicitScopes && activeCatalogAliId && !activeCatalogOwnerId) {
        if (/^[A-Z]{2}-[0-9][A-Z0-9]{2}$/.test(activeCatalogAliId)) {
          params.append('catalogAliId', activeCatalogAliId);
        } else {
          logger.warn('SearchContext: dropping malformed activeCatalogAliId', {
            activeCatalogAliId,
          });
        }
      }

      // Merge keywords into q param
      const qParts = [query, filters?.keywords].filter(Boolean);
      if (qParts.length > 0) {
        params.append('q', qParts.join(' '));
      }

      if (filters?.title) {
        params.append('title', filters.title);
      }

      const appendArrayParam = (key: string, values: string[] | undefined) => {
        if (values && values.length > 0) {
          params.append(key, values.join(','));
        }
      };

      appendArrayParam('workType', filters?.workType);
      appendArrayParam('materials', filters?.materials);
      appendArrayParam('techniques', filters?.techniques);
      appendArrayParam('creators', filters?.creators);
      appendArrayParam('decade', filters?.decade);

      if (filters?.creationYear?.min) {
        params.append('yearFrom', filters.creationYear.min.toString());
      }

      if (filters?.creationYear?.max) {
        params.append('yearTo', filters.creationYear.max.toString());
      }

      if (filters?.provenance) {
        params.append('provenance', filters.provenance);
      }

      if (filters?.heightFrom !== undefined) {
        params.append('heightFrom', filters.heightFrom.toString());
      }

      if (filters?.heightTo !== undefined) {
        params.append('heightTo', filters.heightTo.toString());
      }

      if (filters?.widthFrom !== undefined) {
        params.append('widthFrom', filters.widthFrom.toString());
      }

      if (filters?.widthTo !== undefined) {
        params.append('widthTo', filters.widthTo.toString());
      }

      if (filters?.dateFrom !== undefined) {
        params.append('dateFrom', filters.dateFrom.toString());
      }

      if (filters?.dateTo !== undefined) {
        params.append('dateTo', filters.dateTo.toString());
      }

      const effectiveSortBy = filters?.sortBy || sortByRef.current;
      if (effectiveSortBy && effectiveSortBy !== 'relevance') {
        params.append('sortBy', effectiveSortBy);
      }

      if (filters?.requestedFacets && filters.requestedFacets.length > 0) {
        params.append('facets', filters.requestedFacets.join(','));
      }

      if (filters?.scopes && filters.scopes.length > 0) {
        // Use serializeScopes to handle the structured `delegate:owner:coll`
        // wire form alongside simple string scopes — `.join(',')` would
        // produce '[object Object]' for the structured variant.
        params.append('scopes', serializeScopes(filters.scopes));
      }

      params.append('size', pageSize.toString());
      params.append('from', (page * pageSize).toString());

      // Use GET request to match backend API
      const response = await api.get(`/api/search?${params.toString()}`, {
        signal: controller.signal,
      });

      const results = response.data.items || [];

      // Transform backend results to match frontend interface
      const transformedResults: SearchResult[] = results.map((item: Record<string, unknown>) => ({
        catalogItemId: item.catalogItemId,
        userId: item.userId || '',
        title: item.title,
        creators: item.creators || [],
        workType: item.workType,
        materials: item.materials || [],
        techniques: item.techniques || [],
        keywords: item.keywords || [],
        coverImageUrl: item.coverImageUrl,  // Let rendering component handle fallback
        aliCatalogNumber: item.aliCatalogNumber as string | undefined,
        visibility: (item.visibility as 'public' | 'private') || 'private',
        creationYear: item.creationYear,
        updatedAt: item.updatedAt,
        highlight: item.highlight as Record<string, string[]> | undefined,
      }));

      setSearchResults(transformedResults);
      setTotal(response.data.total || 0);

      // Parse facet buckets from the opt-in backend shape.
      // Backend returns `facets: null` or the field is omitted when the
      // caller didn't request any — treat both as null.
      const rawFacets = response.data.facets;
      if (rawFacets && typeof rawFacets === 'object') {
        setFacetBuckets(rawFacets as Record<string, FacetBucket[]>);
      } else {
        setFacetBuckets(null);
      }

      return transformedResults;
    } catch (err: unknown) {
      // Don't treat cancellation as an error
      if (axios.isCancel(err)) {
        return [];
      }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      if (!controller.signal.aborted && mountedRef.current) {
        setIsSearching(false);
      }
    }
  }, [page, pageSize, activeCatalogOwnerId, activeCatalogAliId]);

  const suggest = useCallback(async (query: string, field: string): Promise<string[]> => {
    if (!api) throw new Error('API not configured');

    try {
      const response = await api.get(`/api/suggest?field=${field}&q=${encodeURIComponent(query)}`);
      const fieldSuggestions = response.data.suggestions || [];

      setSuggestions(prev => ({ ...prev, [field]: fieldSuggestions }));
      return fieldSuggestions;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      return [];
    }
  }, []);

  const suggestCombined = useCallback(async (query: string): Promise<string[]> => {
    if (!api) throw new Error('API not configured');

    try {
      const response = await api.get(`/api/suggest-combined?q=${encodeURIComponent(query)}`);
      const combinedSuggestions = response.data.suggestions || [];

      setSuggestions(prev => ({ ...prev, combined: combinedSuggestions }));
      return combinedSuggestions;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      return [];
    }
  }, []);

  return (
    <SearchContext.Provider value={{
      // Basic search
      searchTerm,
      setSearchTerm,

      // Advanced search operations
      search,
      suggest,
      suggestCombined,

      // State management
      searchResults,
      suggestions,
      facets,
      facetBuckets,
      isSearching,
      error,

      // Pagination and sort
      total,
      page,
      pageSize,
      setPage,
      setPageSize,
      sortBy,
      setSortBy,
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export { SearchProvider };
